import { markRaw } from 'vue'
import { defineStore } from 'pinia'
import { useLocalStorage } from '@vueuse/core'
import type { RemovableRef } from '@vueuse/core'
import { debounce } from 'lodash'
import * as Sentry from '@sentry/browser'
import { Device, Call } from '@twilio/voice-sdk'
import { Service } from '@/services/helpers/ServiceHelper'
import useSnackbar from '@/composables/useSnackbar'
import useDialog from '@/composables/useDialog'
import type { Contact } from '@/services/types/Contact'

export const useCallStore = defineStore('call', () => {
  const snackbar = useSnackbar()
  const dialog = useDialog()
  const getToken = () => Service.$axiosInstance.$get('/api/sms-threads/token')

  // # MIC PERMISSION
  const micPermission = ref<PermissionStatus | null>(null)
  const setMicPermission = async () => {
    try {
      micPermission.value = await navigator.permissions.query({ name: 'microphone' as PermissionName })
      if (micPermission.value.state === 'granted') {
        setCallDevice()
      } else if (micPermission.value.state === 'prompt') {
        dialog.info({
          title: 'Microphone Permission Required',
          text: 'We need access to your microphone to make and receive calls. Please allow microphone access when prompted by your browser.',
          action: async () => {
            await navigator.mediaDevices.getUserMedia({ audio: true })
              .then(() => {
                snackbar.success('Microphone permission granted')
                setCallDevice()
              })
              .catch(() => snackbar.error('Microphone permission denied'))
          },
          dialogProps: {
            confirmText: 'Understood',
            showCancel: false,
            confirmButtonProps: { block: true }
          }
        })
      } else if (micPermission.value.state === 'denied') {
        dialog.error({
          title: 'Microphone Permission Blocked',
          text: "We've detected that you've blocked microphone access by default, so no prompt will appear to request it. To make and receive calls, please enable microphone access manually in your browser settings.",
          dialogProps: {
            confirmText: 'Understood',
            showCancel: false,
            confirmButtonProps: { block: true }
          }
        })
      }
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to set microphone permission')
    }
  }
  onMounted(async () => await setMicPermission())

  // # CALL DEVICE
  const callDevice = ref<Device | null>(null)
  const removeCallDevice = async () => {
    await callDevice.value?.unregister()
    callDevice.value?.removeAllListeners()
    removeAudioSources()
    removeIncomingCall()
    removeOutgoingCall()
    callDevice.value = null
  }
  const setCallDevice = async () => {
    try {
      if (callDevice.value) { return }
      const token = await getToken()
      callDevice.value = markRaw(new Device(token))
      callDevice.value?.on('error', (error) => {
        Sentry.captureException(error)
        snackbar.error('There was an error with the call device')
        removeCallDevice()
      })
      callDevice.value?.on('tokenWillExpire', async () => {
        const token = await getToken()
        callDevice.value?.updateToken(token)
      })
      listenToIncomingCalls()
      await Promise.all([
        callDevice.value?.register(),
        setAudioSources()
      ])
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to get device')
      removeCallDevice()
    }
  }

  // # CALL TIMER
  const callTimer = ref<NodeJS.Timeout | null>(null)
  const callDuration = ref(0)
  const startCallTimer = () => {
    callDuration.value = 0
    callTimer.value = setInterval(() => {
      callDuration.value++
    }, 1000)
  }
  const stopCallTimer = () => {
    if (callTimer.value) {
      clearInterval(callTimer.value)
      callTimer.value = null
    }
  }

  // # INCOMING CALL
  const incomingCall = ref<Call | null>(null)
  const incomingCallContact = ref<Contact | null>(null)
  const incomingCallAccepted = ref(false)
  const incomingCallMuted = ref(false)
  const removeIncomingCall = () => {
    stopCallTimer()
    incomingCall.value?.removeAllListeners()
    endIncomingCall()
    incomingCall.value = null
  }
  const listenToIncomingCalls = () => {
    try {
      callDevice.value?.on('incoming', (call: Call) => {
        if (outgoingCall.value) {
          endIncomingCall()
          return
        }
        incomingCallAccepted.value = false
        incomingCallMuted.value = false
        incomingCall.value = markRaw(call)
        incomingCall.value.on('error', (error) => {
          Sentry.captureException(error)
          snackbar.error('There was an error with the incoming call')
          removeIncomingCall()
        })
        const endEvents = ['cancel', 'reject', 'disconnect']
        endEvents.forEach(event => incomingCall.value?.on(event, () => removeIncomingCall()))
        incomingCall.value.on('accept', () => {
          incomingCallAccepted.value = true
          startCallTimer()
        })
        // TODO: get the contact from the database
        incomingCallContact.value = {
          id: '',
          created: new Date(),
          tenant_id: '',
          phone_number_id: '',
          phone: incomingCall.value?.parameters.From || '',
          name: 'Unknown',
          email: '',
          tags: [],
          tags_expanded: [],
          sms_threads: [],
          parent: null,
          is_blacklisted: false,
          blocked: null,
          client_can_optin: false
        }
      })
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to listen to incoming calls')
      removeIncomingCall()
    }
  }
  const acceptIncomingCall = () => {
    incomingCall.value?.accept()
  }
  const endIncomingCall = () => {
    const pending = incomingCall.value?.status() === 'pending'
    pending ? incomingCall.value?.reject() : incomingCall.value?.disconnect()
  }
  const toggleMuteIncomingCall = () => {
    incomingCallMuted.value = !incomingCallMuted.value
    incomingCall.value?.mute(incomingCallMuted.value)
  }

  // # OUTGOING CALL
  const outgoingCall = ref<Call | null>(null)
  const outgoingCallContact = ref<Contact | null>(null)
  const outgoingCallAccepted = ref(true)
  const outgoingCallMuted = ref(false)
  const removeOutgoingCall = () => {
    stopCallTimer()
    outgoingCall.value?.removeAllListeners()
    endOutgoingCall()
    outgoingCall.value = null
  }
  const makeOutgoingCall = async (contact: Contact, from: string) => {
    try {
      if (incomingCall.value || outgoingCall.value) { return }
      outgoingCallAccepted.value = false
      outgoingCallMuted.value = false
      const call = await callDevice.value?.connect({ params: { To: contact.phone, From: from } })
      outgoingCall.value = call ? markRaw(call) : null
      outgoingCall.value?.on('error', (error) => {
        Sentry.captureException(error)
        snackbar.error('There was an error with the outgoing call')
        removeOutgoingCall()
      })
      outgoingCall.value?.on('disconnect', () => removeOutgoingCall())
      outgoingCallContact.value = contact
      const onAccept = () => {
        // TODO: you can't get the real accept status for outgoing calls from the JS SDK -> https://stackoverflow.com/questions/42528985/how-do-i-find-out-when-a-twilio-call-has-actually-been-answered-when-using-the-j
        outgoingCallAccepted.value = true
        startCallTimer()
      }
      onAccept()
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to make outgoing call')
      removeOutgoingCall()
    }
  }
  const endOutgoingCall = () => outgoingCall.value?.disconnect()
  const toggleMuteOutgoingCall = () => {
    outgoingCallMuted.value = !outgoingCallMuted.value
    outgoingCall.value?.mute(outgoingCallMuted.value)
  }
  const callInProgress = computed(() => !!incomingCall.value || !!outgoingCall.value)

  // # AUDIO SOURCES
  const storedInputSource: RemovableRef<string> = useLocalStorage('inputSource', 'default')
  const storedOutputSource: RemovableRef<string> = useLocalStorage('outputSource', 'default')
  const availableInputSources = ref<MediaDeviceInfo[]>([])
  const inputSource = ref<string | null>(null)
  const availableOutputSources = ref<MediaDeviceInfo[]>([])
  const outputSource = ref<string | null>(null)
  const removeAudioSources = () => {
    callDevice.value?.audio?.removeAllListeners()
    availableInputSources.value = []
    inputSource.value = null
    availableOutputSources.value = []
    outputSource.value = null
  }
  const setAudioSources = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true })
      if (!callDevice.value?.audio) { throw new Error('Device audio not found') }
      callDevice.value?.audio.removeAllListeners()
      callDevice.value?.audio.on('deviceChange', () => setAudioSourcesDebounced())
      availableInputSources.value = Array.from(callDevice.value?.audio.availableInputDevices.values())
      const isStoredInputSourceAvailable = availableInputSources.value.some((source) => {
        return source.deviceId === storedInputSource.value
      })
      inputSource.value = isStoredInputSourceAvailable ? storedInputSource.value : 'default'
      availableOutputSources.value = Array.from(callDevice.value?.audio.availableOutputDevices.values())
      const isStoredOutputSourceAvailable = availableOutputSources.value.some((source) => {
        return source.deviceId === storedOutputSource.value
      })
      outputSource.value = isStoredOutputSourceAvailable ? storedOutputSource.value : 'default'
      await Promise.all([
        callDevice.value?.audio.setInputDevice(inputSource.value),
        callDevice.value?.audio.speakerDevices.set(outputSource.value),
        callDevice.value?.audio.ringtoneDevices.set(outputSource.value)
      ])
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to get audio sources')
      removeAudioSources()
    }
  }
  const setAudioSourcesDebounced = debounce(setAudioSources, 1000)
  const changeInputSource = async (deviceId: string) => {
    try {
      await callDevice.value?.audio?.setInputDevice(deviceId)
      storedInputSource.value = deviceId
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to set input source')
    }
  }
  const changeOutputSource = async (deviceId: string) => {
    try {
      await Promise.all([
        callDevice.value?.audio?.speakerDevices.set(deviceId),
        callDevice.value?.audio?.ringtoneDevices.set(deviceId)
      ])
      storedOutputSource.value = deviceId
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to set output source')
    }
  }

  return {
    micPermission,
    callDevice,
    callDuration,
    incomingCall,
    incomingCallContact,
    incomingCallAccepted,
    incomingCallMuted,
    acceptIncomingCall,
    endIncomingCall,
    toggleMuteIncomingCall,
    outgoingCall,
    outgoingCallContact,
    outgoingCallAccepted,
    outgoingCallMuted,
    makeOutgoingCall,
    endOutgoingCall,
    toggleMuteOutgoingCall,
    callInProgress,
    availableInputSources,
    inputSource,
    availableOutputSources,
    outputSource,
    changeInputSource,
    changeOutputSource
  }
})
